/**
 * IndexControl - Renders index slider controls
 *
 * Handles both pill and regular slider variants for index controls.
 * Uses LayoutConfigContext to get index values instead of relying on additionalProps.
 */

import {includes} from 'lodash';
import React, {useRef} from 'react';
import {twMerge} from 'tailwind-merge';

import {useLayoutConfig} from '../layout/contexts/LayoutConfigContext';
import {useMediaBrowserLoading} from '../MediaBrowserLoadingContext';
import {CARD_DEFAULT_CLASSNAMES} from './cardStyles';
import {GenericSliderDropdown} from './GenericSliderDropdown';
import {Slider} from './Slider';
import {Title} from './Title';
import {type IndexControlProps} from './types';

export type {IndexControlProps};

export const IndexControl: React.FC<IndexControlProps> = ({
  control,
  index,
  stepMeta,
  columnIndex,
  rowIndex,
  indexSliderKeyboardBindings,
  activeAxes,
  config,
  layout,
}) => {
  const layoutConfig = useLayoutConfig();
  const loading = useMediaBrowserLoading();
  const indexValue = layoutConfig?.getIndex(columnIndex, rowIndex) ?? 0;
  const cachedSliderProps = useRef<{
    min: number;
    max: number;
    available: number[];
    value: number;
  } | null>(null);

  const defaultClassNames = twMerge(
    CARD_DEFAULT_CLASSNAMES,
    control.classNames
  );

  if (control.type === 'label') {
    if (indexValue === undefined) {
      return null;
    }

    return (
      <Title
        key={`index-label-${index}`}
        label="Index"
        value={indexValue}
        className={defaultClassNames}
        labelPosition={control.labelPosition ?? 'inline'}
        showColon={config.mode === 'compare'}
        justifyContent={config.mode === 'compare' ? 'center' : 'start'}
      />
    );
  }

  // Index is on an axis, so don't show slider
  if (includes(activeAxes, 'index')) {
    return null;
  }

  const handleIndexChange = (value: number) => {
    if (layoutConfig) {
      layoutConfig.setIndex(value, columnIndex);
    }
  };

  // Use available indices from context (pass columnIndex for column-specific indices)
  // This ensures we get the correct indices for the column's run
  const availableIndices = layoutConfig?.getAvailableIndices(columnIndex) ??
    stepMeta?.indices ?? [0];

  // Keep the cache up-to-date whenever real data is present so we can display
  // it during loading transitions instead of replacing the slider with a pulse.
  if (availableIndices.length >= 1) {
    // oxlint-disable-next-line react/refs
    cachedSliderProps.current = {
      min: availableIndices[0],
      max: availableIndices[availableIndices.length - 1],
      available: availableIndices,
      value: indexValue,
    };
  }

  const isFrozen = availableIndices.length < 1 && loading;

  if (availableIndices.length < 1 && !loading) {
    return null;
  }

  // oxlint-disable-next-line react/refs
  if (isFrozen && !cachedSliderProps.current) {
    // No cached data yet (first load): fall back to pulse skeleton
    return (
      <div
        className={twMerge(
          defaultClassNames,
          'h-[50px] rounded-md bg-[light-dark(var(--moon-150),var(--moon-800))] animate-pulse'
        )}
      />
    );
  }

  // Use last-known slider state when frozen, live data otherwise
  const {min, max, available, value} = isFrozen
    ? cachedSliderProps.current!
    : {
        min: availableIndices[0],
        max: availableIndices[availableIndices.length - 1],
        available: availableIndices,
        value: indexValue,
      };

  const sliderClassName = isFrozen
    ? twMerge(defaultClassNames, 'opacity-50 pointer-events-none')
    : defaultClassNames;

  // Only include keyboard bindings when control is linked (no columnIndex)
  // Individual controls shouldn't have keyboard bindings to avoid conflicts
  const keyboardBindings =
    columnIndex == null && !isFrozen ? indexSliderKeyboardBindings : undefined;

  // Render card variant with popover
  if (control.variant === 'card') {
    return (
      <GenericSliderDropdown
        label="Index"
        value={value}
        min={min}
        max={max}
        availableValues={available}
        snapToAvailableValues={true}
        strideLength={1}
        onChange={isFrozen ? () => {} : handleIndexChange}
        keyboardBindings={keyboardBindings}
        className={sliderClassName}
        data-test={isFrozen ? undefined : 'index-slider-card'}
        layout={layout}
      />
    );
  }

  // Use generic Slider component for standard variant
  return (
    <Slider
      className={sliderClassName}
      min={min}
      max={max}
      minLabel={min.toString()}
      maxLabel={max.toString()}
      current={value}
      values={available}
      label="Index"
      snapToExistingValue={true}
      strideLength={1}
      keyboardBindings={keyboardBindings}
      onChange={isFrozen ? () => {} : handleIndexChange}
      data-test={isFrozen ? undefined : 'index-slider'}
      layout={layout}
    />
  );
};
